import { useCallback, useEffect, useState } from "react";
import { RefreshCw } from "lucide-react";
import { toast } from "sonner";
import api, { errText } from "@/lib/api";
import CrmShell from "@/components/crm/CrmShell";
import KanbanBoard from "@/components/crm/KanbanBoard";
import LeadDrawer from "@/components/crm/LeadDrawer";
import { inputCls } from "@/components/crm/ui";
import { useAuth } from "@/context/AuthContext";

export default function Pipeline() {
  const { isAdmin } = useAuth();
  const [leads, setLeads] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [owner, setOwner] = useState("all");
  const [temp, setTemp] = useState("all");
  const [openId, setOpenId] = useState(null);

  const load = useCallback(async () => {
    const params = {};
    if (owner !== "all") params.assigned_to = owner;
    if (temp !== "all") params.temperature = temp;
    try {
      const { data } = await api.get("/leads", { params });
      setLeads(data);
    } catch (e) {
      toast.error(errText(e));
    }
  }, [owner, temp]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    const poll = setInterval(load, 20000);
    return () => clearInterval(poll);
  }, [load]);

  useEffect(() => {
    api.get("/users").then(({ data }) => setEmployees(data.filter((u) => u.role === "employee")));
  }, []);

  const move = async (leadId, status) => {
    setLeads((prev) => prev.map((l) => (l.id === leadId ? { ...l, status } : l)));
    try {
      await api.patch(`/leads/${leadId}`, { status });
      toast.success("Stage updated");
    } catch (e) {
      toast.error(errText(e));
      load();
    }
  };

  const contact = async (leadId, channel) => {
    try {
      await api.post(`/leads/${leadId}/contact?channel=${channel}`);
      load();
    } catch (e) {
      toast.error(errText(e));
    }
  };

  return (
    <CrmShell
      title="Lead pipeline"
      subtitle="Drag a card to move a lead through the stages"
      actions={
        <div className="flex gap-2">
          <button
            onClick={load}
            data-testid="refresh-pipeline-btn"
            className="flex items-center gap-2 rounded-full border border-line bg-white px-4 py-2 text-xs font-bold text-ink hover:border-brand"
          >
            <RefreshCw className="h-3.5 w-3.5" /> Refresh
          </button>
          {isAdmin && (
            <select
              value={owner}
              onChange={(e) => setOwner(e.target.value)}
              data-testid="pipeline-owner-filter"
              className={`${inputCls} w-auto`}
            >
              <option value="all" className="bg-white">All advisors</option>
              <option value="unassigned" className="bg-white">Unassigned</option>
              {employees.map((e2) => (
                <option key={e2.id} value={e2.id} className="bg-white">{e2.name}</option>
              ))}
            </select>
          )}
          <select
            value={temp}
            onChange={(e) => setTemp(e.target.value)}
            data-testid="pipeline-temp-filter"
            className={`${inputCls} w-auto`}
          >
            <option value="all" className="bg-white">All temps</option>
            {["hot", "warm", "cold"].map((t) => (
              <option key={t} value={t} className="bg-white">{t}</option>
            ))}
          </select>
        </div>
      }
    >
      <KanbanBoard leads={leads} onMove={move} onOpen={setOpenId} onContact={contact} />
      <LeadDrawer
        leadId={openId}
        open={!!openId}
        onClose={() => setOpenId(null)}
        employees={employees}
        onChanged={load}
      />
    </CrmShell>
  );
}
